import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
  currentTimezone,
  DEFAULT_LOCATION,
  Location,
  LOCATION_SOURCE_RANK,
  LocationSource,
  locationFromGeo,
  locationFromQueryString,
  locationFromTimezone,
} from '../location/location';
import { fetchGeoLocation } from '../services/geo.service';

interface LocationContextValue {
  /** The active US/PR split used to gate jurisdiction-specific UI + features. */
  location: Location;
  /** Whether the active location is Puerto Rico — convenience for consumers. */
  isPuertoRico: boolean;
  /** Where the active location was resolved from (for debugging / precedence). */
  source: LocationSource;
  /**
   * Override the location from a stronger signal. No-op if `nextSource` is less
   * authoritative than the current one (see LOCATION_SOURCE_RANK). Used by
   * LocationAddressSync to push address/user-derived locations up.
   */
  setLocation: (next: Location, nextSource: LocationSource) => void;
}

const LocationContext = createContext<LocationContextValue | undefined>(undefined);

/**
 * Holds the US-vs-PR `location` split, independent of language. Sits high in the
 * tree (above Auth/User/Onboarding, like LocaleProvider) so it is available
 * pre-login. Resolution:
 *   - Initial synchronous guess from the device timezone (PR → PR, else US).
 *   - Corrected asynchronously by an IP geo lookup.
 *   - Later overridden by an entered address via LocationAddressSync, which
 *     calls setLocation with the higher-ranked 'address'/'user' sources.
 * Nothing here is persisted — Address is the server-side source of truth.
 */
export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const initial = useMemo(() => {
    // `?location=PR` (or US) forces the location and, as the highest-ranked
    // source, holds through IP/address/user resolution. Intentionally honored in
    // ALL environments, including production — note this lets anyone override the
    // jurisdiction split via the query string.
    const override =
      typeof window !== 'undefined' ? locationFromQueryString(window.location.search) : undefined;
    if (override) return { location: override, source: 'override' as const };

    const tz = locationFromTimezone(currentTimezone());
    return tz ? { location: tz, source: 'timezone' as const } : { location: DEFAULT_LOCATION, source: 'default' as const };
  }, []);

  const [location, setLocationState] = useState<Location>(initial.location);
  const [source, setSourceState] = useState<LocationSource>(initial.source);
  // Mirror of `source` readable synchronously inside setLocation without adding
  // it to the callback's dependency list (keeps setLocation identity stable).
  const sourceRef = useRef<LocationSource>(initial.source);

  const setLocation = useCallback((next: Location, nextSource: LocationSource) => {
    if (LOCATION_SOURCE_RANK[nextSource] < LOCATION_SOURCE_RANK[sourceRef.current]) return;
    sourceRef.current = nextSource;
    setSourceState(nextSource);
    setLocationState(next);
  }, []);

  // Best-effort IP lookup. Corrects the timezone/default guess unless a stronger
  // address/user signal has already arrived (guarded by rank in setLocation).
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      const geo = await fetchGeoLocation(controller.signal);
      const derived = geo ? locationFromGeo(geo) : undefined;
      if (derived) setLocation(derived, 'ip');
    })();
    return () => controller.abort();
  }, [setLocation]);

  const value = useMemo<LocationContextValue>(
    () => ({ location, isPuertoRico: location === 'PR', source, setLocation }),
    [location, source, setLocation],
  );

  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
};

export const useLocationContext = (): LocationContextValue => {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error('useLocationContext must be used within a LocationProvider');
  return ctx;
};
